import DOMPurify from 'dompurify';

// DOMPurify configuration for header/footer HTML content
// Allows formatting tags, images, and SVG elements needed for logos
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    // Basic text formatting (TipTap StarterKit)
    'p',
    'br',
    'strong',
    'em',
    'u',
    's',
    'code',
    'pre',
    'blockquote',
    // Headings (TipTap Heading extension with levels 1-3)
    'h1',
    'h2',
    'h3',
    // Lists (TipTap StarterKit)
    'ul',
    'ol',
    'li',
    // Other elements
    'hr',
    'img',
    'div',
    'span',
    // SVG elements for logos
    'svg',
    'path',
    'g',
    'circle',
    'rect',
    'line',
    'polygon',
    'polyline',
    'ellipse',
    'text',
    'tspan',
    'defs',
    'clipPath',
    'use',
    // SVG styling/gradient elements needed for preset logos
    'style',
    'linearGradient',
    'radialGradient',
    'stop',
    'pattern',
    'mask',
  ],
  ALLOWED_ATTR: [
    'style',
    'class',
    'src',
    'width',
    'height',
    'alt',
    'crossorigin',
    'containerstyle',
    'wrapperstyle',
    // SVG attributes
    'xmlns',
    'xmlns:xlink',
    'viewBox',
    'fill',
    'fill-rule',
    'fill-opacity',
    'd',
    'stroke',
    'stroke-width',
    'stroke-linecap',
    'stroke-linejoin',
    'transform',
    'x',
    'y',
    'cx',
    'cy',
    'r',
    'rx',
    'ry',
    'x1',
    'y1',
    'x2',
    'y2',
    'points',
    'preserveAspectRatio',
    'clip-path',
    'clip-rule',
    'id',
    'xlink:href',
    'href',
    // Gradient/stop attributes
    'offset',
    'stop-color',
    'stop-opacity',
    'gradientUnits',
    'gradientTransform',
    'spreadMethod',
    'fx',
    'fy',
    // Mask/pattern attributes
    'maskUnits',
    'maskContentUnits',
    'patternUnits',
    'patternContentUnits',
    'patternTransform',
    'opacity',
  ],
  ALLOW_DATA_ATTR: true,
};

const isPresignedS3Url = (src) => {
  if (!src) return false;

  try {
    const parsedUrl = new URL(src, window.location.origin);
    const host = parsedUrl.hostname.toLowerCase();
    const isS3Host =
      host === 's3.amazonaws.com' ||
      host.startsWith('s3.') ||
      host.endsWith('.amazonaws.com');
    const hasAwsSignature =
      parsedUrl.searchParams.has('X-Amz-Signature') ||
      parsedUrl.searchParams.has('X-Amz-Algorithm');

    return isS3Host && hasAwsSignature;
  } catch (error) {
    return false;
  }
};

/**
 * Sanitize HTML for header/footer content.
 * Strips potentially dangerous content while preserving formatting, images, and SVG logos.
 *
 * Also strips the single trailing <p></p> that TipTap auto-injects after headings (h1–h3,
 * i.e. the Large and Small heading options) so the cursor has somewhere to land. This only applies to headings — trailing paragraphs
 * after other blocks (hr, images, plain paragraphs, etc.) are left untouched. Only ONE
 * trailing empty paragraph is ever removed, so intentional blank lines added by pressing
 * Enter multiple times are preserved. For example:
 *   <h2>Title</h2><p></p>          → <h2>Title</h2>        (auto-injected, stripped)
 *   <h2>Title</h2><p></p><p></p>   → <h2>Title</h2><p></p> (user spacing preserved)
 *   <hr><p></p>                    → <hr><p></p>            (not a heading, unchanged)
 */
export const sanitizeHeaderFooterHtml = (html) => {
  if (!html) return '';
  const sanitized = DOMPurify.sanitize(html, SANITIZE_CONFIG);
  if (typeof document === 'undefined') {
    return sanitized
      .replace(/(<\/h[1-3]>)\s*<p>(?:<br\s*\/?>)?\s*<\/p>\s*$/i, '$1')
      .trim();
  }

  const container = document.createElement('div');
  container.innerHTML = sanitized;

  // Presigned S3 URLs already carry auth in query params; forcing
  // crossorigin=use-credentials can trigger CORS failures.
  container
    .querySelectorAll("img[crossorigin='use-credentials']")
    .forEach((img) => {
      if (isPresignedS3Url(img.getAttribute('src'))) {
        img.removeAttribute('crossorigin');
      }
    });

  return container.innerHTML
    .replace(/(<\/h[1-3]>)\s*<p>(?:<br\s*\/?>)?\s*<\/p>\s*$/i, '$1')
    .trim();
};
